/**
 * Explainability API — typed wrappers for SHAP explainability endpoints
 *
 * Endpoints:
 *   POST /api/v1/explainability/explain — single anomaly SHAP attributions
 *   POST /api/v1/explainability/explain-batch — multiple anomalies
 *   GET  /api/v1/explainability/feature-importance — global feature importance
 *
 * Design spec: docs/plans/2026-06-25-redesign-design-spec.md
 *   - Zero math changes — results displayed verbatim
 *   - All monetary values via FinancialValue component
 */

import { apiFetch } from '@/utils/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ShapAttribution {
  /** Feature name (e.g., 'transaction_amount', 'merchant_category') */
  feature: string;
  /** SHAP attribution value — magnitude indicates importance, sign indicates direction */
  attribution: number;
  /** Human-readable feature description */
  description?: string;
  /** Whether this feature represents a monetary value */
  is_monetary?: boolean;
}

export interface ExplainabilityResponse {
  /** Anomaly ID that was explained */
  anomaly_id: string;
  /** SHAP attributions sorted by absolute magnitude (descending) */
  attributions: ShapAttribution[];
  /** Base value (expected value) from the model */
  base_value: number;
  /** Predicted output value for this anomaly */
  output_value: number;
  /** Model version used for explanation */
  model_version: string;
  /** ISO timestamp of explanation generation */
  generated_at: string;
}

export interface ExplainBatchRequest {
  /** Anomaly IDs to explain */
  anomaly_ids: string[];
  /** Optional feature context to constrain explanation */
  feature_context?: Record<string, unknown>;
}

export interface ExplainBatchResponse {
  /** Explanations keyed by anomaly_id */
  explanations: Record<string, ExplainabilityResponse>;
  /** Errors for any failed explanations, keyed by anomaly_id */
  errors?: Record<string, string>;
}

export interface FeatureImportance {
  /** Feature name */
  feature: string;
  /** Global importance score (mean absolute SHAP value across dataset) */
  importance: number;
  /** Human-readable feature description */
  description?: string;
  /** Whether this feature represents a monetary value */
  is_monetary?: boolean;
}

export interface FeatureImportanceResponse {
  /** Global feature importances sorted by importance (descending) */
  features: FeatureImportance[];
  /** Total number of features in model */
  total_features: number;
  /** Number of samples used to compute importance */
  sample_count: number;
  /** Model version */
  model_version: string;
  /** ISO timestamp of computation */
  generated_at: string;
}

export interface ExplainRequest {
  /** Anomaly ID to explain */
  anomaly_id: string;
  /** Optional feature context to constrain explanation (e.g., specific time range) */
  feature_context?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// API Functions
// ---------------------------------------------------------------------------

const EXPLAINABILITY_BASE = '/api/v1/explainability';

/**
 * Get SHAP explanation for a single anomaly.
 *
 * @param request - ExplainRequest with anomaly_id and optional feature_context
 * @returns ExplainabilityResponse with SHAP attributions
 */
export async function explainAnomaly(
  request: ExplainRequest
): Promise<ExplainabilityResponse> {
  return apiFetch<ExplainabilityResponse>(`${EXPLAINABILITY_BASE}/explain`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
}

/**
 * Get SHAP explanations for multiple anomalies in a batch.
 *
 * @param request - ExplainBatchRequest with anomaly_ids array
 * @returns ExplainBatchResponse with explanations keyed by anomaly_id
 */
export async function explainAnomalyBatch(
  request: ExplainBatchRequest
): Promise<ExplainBatchResponse> {
  return apiFetch<ExplainBatchResponse>(`${EXPLAINABILITY_BASE}/explain-batch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
}

/**
 * Get global feature importance across all anomalies.
 *
 * @returns FeatureImportanceResponse with ranked feature importances
 */
export async function getFeatureImportance(): Promise<FeatureImportanceResponse> {
  return apiFetch<FeatureImportanceResponse>(`${EXPLAINABILITY_BASE}/feature-importance`, {
    method: 'GET',
  });
}

/**
 * Convenience hook-compatible object for destructured imports.
 *
 * Usage:
 *   import { explainabilityApi } from '@/api/explainabilityApi';
 *   const result = await explainabilityApi.explain({ anomaly_id: 'abc' });
 */
export const explainabilityApi = {
  explain: explainAnomaly,
  explainBatch: explainAnomalyBatch,
  getFeatureImportance,
} as const;

export default explainabilityApi;
